import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Configure axios defaults for better performance
axios.defaults.timeout = 15000; // 15 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add response interceptor for better error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', error.config?.url);
    } else if (error.response?.status === 401) {
      // Handle unauthorized - might need to logout
      console.error('Unauthorized request:', error.config?.url);
    }
    return Promise.reject(error);
  }
);
import '@/App.css';
import Login from '@/pages/Login';
import BrokerDashboard from '@/pages/BrokerDashboard';
import ManagerDashboard from '@/pages/ManagerDashboard';
import LeadList from '@/pages/LeadList';
import LeadDetail from '@/pages/LeadDetail';
import CreateLead from '@/pages/CreateLead';
import Reports from '@/pages/Reports';
import BrokerManagement from '@/pages/BrokerManagement';
import ProjectManagement from '@/pages/ProjectManagement';
import ManagementPage from '@/pages/ManagementPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import SettingsPage from '@/pages/SettingsPage';
import UserManagement from '@/pages/UserManagement';
import AuditLogs from '@/pages/AuditLogs';
import DuplicateLeads from '@/pages/DuplicateLeads';
import CalendarView from '@/pages/CalendarView';
import { Toaster } from '@/components/ui/sonner';
import { NavigationProvider } from '@/contexts/NavigationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setUser(res.data);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem('token');
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    // Clear all navigation and filter state on logout
    localStorage.removeItem('nav_previous_page');
    // Clear sessionStorage (filter state for LeadList)
    sessionStorage.clear();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <NavigationProvider>
      <AuthContext.Provider value={{ user, login, logout }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
            <Route path="/" element={user ? (
              user.user_role === 'Manager' || user.user_role === 'Super Administrator' ? 
              <ManagerDashboard /> : <BrokerDashboard />
            ) : <Navigate to="/login" />} />
            <Route path="/leads" element={user ? <LeadList /> : <Navigate to="/login" />} />
            <Route path="/leads/new" element={user ? <CreateLead /> : <Navigate to="/login" />} />
            <Route path="/leads/:id" element={user ? <LeadDetail /> : <Navigate to="/login" />} />
            <Route path="/reports" element={user ? <Reports /> : <Navigate to="/login" />} />
            <Route path="/brokers" element={user ? <BrokerManagement /> : <Navigate to="/login" />} />
            <Route path="/projects" element={user ? <ProjectManagement /> : <Navigate to="/login" />} />
            <Route path="/management" element={user ? <ManagementPage /> : <Navigate to="/login" />} />
            <Route path="/integrations" element={user ? <IntegrationsPage /> : <Navigate to="/login" />} />
            <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/login" />} />
            <Route path="/users/reset-password" element={user ? <UserManagement /> : <Navigate to="/login" />} />
            <Route path="/audit-logs" element={user ? <AuditLogs /> : <Navigate to="/login" />} />
            <Route path="/duplicate-leads" element={user ? <DuplicateLeads /> : <Navigate to="/login" />} />
            <Route path="/calendar" element={user ? <CalendarView /> : <Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthContext.Provider>
    </NavigationProvider>
  );
}

export default App;
